import numeral from 'numeral';
import { Record } from 'immutable';

import Hydrator from 'utils/Hydrator';

const AccountRecord = Record({
  id: 0,
  accountId: 0,
  name: null,
  fullName: null,
  accountNumber: null,
  bankNumber: null,
  accountType: null,
  classification: null,
  balance: null,
  currency: null,
  accountCreateTime: new Date(0),
  accountUpdateTime: new Date(0),
  viewedOnMonitor: null,
  provider: null,
});

export default class Account extends AccountRecord {
  static mapProperties = {
    accountCreateTime: Hydrator.timestamp2Date,
    accountUpdateTime: Hydrator.timestamp2Date,
    accountType: (accountType = '') => accountType.toLowerCase(),
  };

  static deserialize(jsonObject = {}) {
    const keyTransformer = {
      id: 'accountId',
    };

    const additionalProperties = {
      id: jsonObject.id,
    };

    const hydrated = Object.keys(jsonObject).reduce(
      Hydrator.assign(jsonObject, Account.mapProperties, keyTransformer),
      additionalProperties,
    );

    return new Account(hydrated);
  }

  static deserializeSnapshot(jsonObject = {}) {
    const keyTransformer = {
      ingestorId: 'accountId',
    };

    const hydrated = Object.keys(jsonObject).reduce(
      Hydrator.assign(jsonObject, Account.mapProperties, keyTransformer),
      {},
    );

    return new Account(hydrated);
  }

  get shouldBePromptedAsNew() {
    return (
      !this.viewedOnMonitor &&
      (this.isBankAccount ||
        ((this.isInterestAccount || this.isRepaymentAccount) && Math.abs(this.balance) > 0))
    );
  }

  get isBankAccount() {
    const bankAccountTypes = ['bank', 'cash at bank and in hand'];

    return bankAccountTypes.includes(this.accountType);
  }

  get isCreditCard() {
    return this.accountType === 'credit card';
  }

  get isCreditLine() {
    return this.balance < 0 && this.name.toLowerCase().indexOf('loan') >= 0;
  }

  get isInterestAccount() {
    return (
      this.fullName.toLowerCase().includes('interest') ||
      this.name.toLowerCase().includes('interest')
    );
  }

  get isRepaymentAccount() {
    return (
      this.fullName.toLowerCase().includes('repayment') ||
      this.name.toLowerCase().includes('repayment')
    );
  }

  get accountIdFormatted() {
    return numeral(this.accountId).format('00000');
  }

  get accountLabel() {
    return this.accountId || this.accountNumber || '0000';
  }

  toOption() {
    return {
      value: {
        name: this.fullName,
        accountId: this.accountLabel,
        accountNumber: this.accountNumber || '',
        bankNumber: this.bankNumber || '',
      },
      label: this.fullName,
    };
  }
}
